/**
 * Atlas curation API client (ATLAS-15) — the authenticated `/api/v1/curation` routes of
 * `services/atlas`: member proposals (ATLAS-16 consumes these) and the editor queue/transitions.
 * Separate from the public, token-free `AtlasClient`: every route here authenticates.
 */
import { ATLAS_CURATION_RELATION_VIOLATION_CODES } from '@architeia/types/atlas';
import { BaseApiClient } from '../client/base-client.js';
import { ApiClientError } from '../types.js';
/**
 * No curation write is ever replayed automatically (ATLAS-15 D8). A lost-response replay of a
 * transition or edit reports a spurious `REVISION_CONFLICT`/`INVALID_TRANSITION` for a write that
 * actually landed, and a proposal replay could create a second draft. Passed per call, this
 * overrides even a caller-supplied `allowNonIdempotentRetry`.
 */
const NO_AUTOMATIC_RETRY = {
    retries: 0,
    retryDelay: 0
};
const CURATION_BASE = '/api/v1/curation';
/** The HTTP status carried by either an `ApiClientError` (what `BaseApiClient` hands to a retry
 * condition) or a raw axios error (`response.status`); `undefined` when there was no response. */
function statusOfError(error) {
    if (!isRecord(error))
        return undefined;
    if (typeof error.statusCode === 'number')
        return error.statusCode;
    const response = error.response;
    if (isRecord(response) && typeof response.status === 'number')
        return response.status;
    return undefined;
}
/**
 * ATLAS-16 (D1) — never retry a 429, whatever retry config the caller supplies. The limiter's
 * window is measured in minutes; retrying ~500 ms later is guaranteed to fail and spends quota.
 * `BaseApiClient.shouldRetry` still retries 429 for GET/PUT, and stays unchanged (it is shared by
 * Kora, Secret Org and Mingle Mage), so this wraps the `retryCondition` for this client only. With
 * no caller `retryCondition`, the default here is "no response, or a 5xx".
 */
function withoutRateLimitRetry(retry) {
    if (retry === undefined)
        return undefined;
    const callerCondition = retry.retryCondition;
    return {
        ...retry,
        retryCondition: (error, requestConfig) => {
            const status = statusOfError(error);
            if (status === 429)
                return false;
            if (callerCondition)
                return callerCondition(error, requestConfig);
            return status === undefined || status >= 500;
        }
    };
}
export function toAtlasCurationPageSearchParams(query) {
    const params = new URLSearchParams();
    if (query.page !== undefined)
        params.set('page', String(query.page));
    if (query.limit !== undefined)
        params.set('limit', String(query.limit));
    return params;
}
export function toAtlasCurationQueueSearchParams(query) {
    const params = new URLSearchParams();
    if (query.status !== undefined)
        params.set('status', query.status);
    if (query.page !== undefined)
        params.set('page', String(query.page));
    if (query.limit !== undefined)
        params.set('limit', String(query.limit));
    return params;
}
/** Structural guard, no cast: the member view never has the key (ATLAS-11 D5). */
export function isAtlasEditorCurationEntryView(view) {
    return typeof view === 'object' && view !== null && 'proposerKoraUserId' in view;
}
export class AtlasCurationClient extends BaseApiClient {
    constructor(config) {
        super({
            ...config,
            retry: withoutRateLimitRetry(config.retry),
            // Same last-resort default as AtlasClient; env-aware resolution lives in the factory.
            baseURL: config.baseURL == null ? 'http://localhost:3006' : config.baseURL
        });
    }
    // ---- Member routes (editors may call them too) ----
    async createProposal(request) {
        return this.post(`${CURATION_BASE}/proposals`, request, undefined, NO_AUTOMATIC_RETRY);
    }
    async listMyProposals(query = {}) {
        return this.get(`${CURATION_BASE}/proposals/mine`, {
            params: toAtlasCurationPageSearchParams(query)
        });
    }
    async getEntry(uuid) {
        return this.get(`${CURATION_BASE}/entries/${encodeURIComponent(uuid)}`);
    }
    async editEntry(uuid, request) {
        return this.put(`${CURATION_BASE}/entries/${encodeURIComponent(uuid)}`, request, undefined, NO_AUTOMATIC_RETRY);
    }
    // ---- Editor routes (`atlas:catalog:review`) ----
    async listQueue(query = {}) {
        return this.get(`${CURATION_BASE}/queue`, {
            params: toAtlasCurationQueueSearchParams(query)
        });
    }
    async listEvents(uuid) {
        return this.get(`${CURATION_BASE}/entries/${encodeURIComponent(uuid)}/events`);
    }
    async publishEntry(uuid, request) {
        return this.post(`${CURATION_BASE}/entries/${encodeURIComponent(uuid)}/publish`, request, undefined, NO_AUTOMATIC_RETRY);
    }
    async rejectEntry(uuid, request) {
        return this.post(`${CURATION_BASE}/entries/${encodeURIComponent(uuid)}/reject`, request, undefined, NO_AUTOMATIC_RETRY);
    }
    async unpublishEntry(uuid, request) {
        return this.post(`${CURATION_BASE}/entries/${encodeURIComponent(uuid)}/unpublish`, request, undefined, NO_AUTOMATIC_RETRY);
    }
    async reopenEntry(uuid, request = {}) {
        return this.post(`${CURATION_BASE}/entries/${encodeURIComponent(uuid)}/reopen`, request, undefined, NO_AUTOMATIC_RETRY);
    }
}
/** Exhaustive by construction: adding a code to `AtlasCurationErrorCode` fails to compile here. */
const KNOWN_ERROR_CODES = {
    INVALID_BODY: true,
    INVALID_QUERY: true,
    MISSING_TOKEN: true,
    INVALID_TOKEN: true,
    IDENTITY_NOT_PROVISIONED: true,
    INSUFFICIENT_PERMISSION: true,
    CURATION_ENTRY_NOT_FOUND: true,
    FIXTURE_READ_ONLY: true,
    INVALID_TRANSITION: true,
    REVISION_CONFLICT: true,
    SLUG_TAKEN: true,
    PROPOSAL_LIMIT_REACHED: true,
    RELATION_INVARIANT_VIOLATED: true,
    RELATION_INVALID: true,
    ALIAS_CONFLICT: true,
    IDENTITY_UNAVAILABLE: true,
    RATE_LIMITED: true,
    CATALOG_UNAVAILABLE: true,
    INTERNAL: true
};
function isCurationErrorCode(value) {
    return Object.prototype.hasOwnProperty.call(KNOWN_ERROR_CODES, value);
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function isCurationStatus(value) {
    return value === 'draft' || value === 'published' || value === 'archived';
}
function isViolationCode(value) {
    return (typeof value === 'string' &&
        ATLAS_CURATION_RELATION_VIOLATION_CODES.some(code => code === value));
}
function readFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
function readViolations(details) {
    const violations = details?.violations;
    if (!Array.isArray(violations))
        return [];
    return violations.map((item) => {
        const record = isRecord(item) ? item : undefined;
        return {
            code: isViolationCode(record?.code) ? record.code : null,
            index: readFiniteNumber(record?.index)
        };
    });
}
function readAliasConflicts(details) {
    const conflicts = details?.conflicts;
    if (!Array.isArray(conflicts))
        return [];
    const result = [];
    conflicts.forEach((item) => {
        if (!isRecord(item))
            return;
        const { field, conflictingSlug } = item;
        if ((field !== 'title' && field !== 'aliases') || typeof conflictingSlug !== 'string')
            return;
        // An older server omits `against` and never reported title-vs-title, so 'alias' is a safe
        // default (it may mislabel an alias-vs-title conflict, which no consumer distinguishes).
        const against = item.against === 'title' ? 'title' : 'alias';
        result.push({ field, against, index: readFiniteNumber(item.index), conflictingSlug });
    });
    return result;
}
function readFields(details) {
    const fields = details?.fields;
    if (!Array.isArray(fields))
        return [];
    return fields.filter((field) => typeof field === 'string');
}
/**
 * Typed interpretation of an `ApiClientError` from the curation API, keyed on the envelope `code`
 * (the HTTP status alone cannot tell four different 409s apart). `null` for anything that is not
 * an `ApiClientError`. Malformed `details` degrade to `null` fields; this never throws.
 *
 * `BaseApiClient` flattens every 5xx to `SERVER_ERROR` (deliberate no-leak choice), so
 * `IDENTITY_UNAVAILABLE` (Kora down) and `CATALOG_UNAVAILABLE` (DB down) are indistinguishable
 * here and both arrive as `server-unavailable`.
 */
export function parseAtlasCurationError(error) {
    if (!(error instanceof ApiClientError))
        return null;
    const statusCode = error.statusCode;
    if (statusCode !== undefined && statusCode >= 500) {
        return { kind: 'server-unavailable', statusCode };
    }
    const code = error.code;
    if (!isCurationErrorCode(code)) {
        // No response at all (network, CORS, timeout): axios's own code survives, e.g. ERR_NETWORK.
        if (statusCode === undefined)
            return { kind: 'unreachable' };
        return { kind: 'unknown', statusCode, code };
    }
    // A recognised curation code always arrives with an HTTP status; if it somehow does not, fall
    // through to the generic 'coded' shape rather than inventing one.
    if (statusCode === undefined)
        return { kind: 'coded', statusCode, code };
    const details = error.details;
    switch (code) {
        case 'REVISION_CONFLICT':
            return {
                kind: 'revision-conflict',
                statusCode,
                currentRevision: readFiniteNumber(details?.currentRevision)
            };
        case 'INVALID_TRANSITION':
            return {
                kind: 'invalid-transition',
                statusCode,
                status: isCurationStatus(details?.status) ? details.status : null
            };
        case 'RELATION_INVALID':
            return { kind: 'relation-invalid', statusCode, violations: readViolations(details) };
        case 'RELATION_INVARIANT_VIOLATED':
            return {
                kind: 'relation-invariant-violated',
                statusCode,
                violations: readViolations(details)
            };
        case 'ALIAS_CONFLICT':
            return { kind: 'alias-conflict', statusCode, conflicts: readAliasConflicts(details) };
        case 'PROPOSAL_LIMIT_REACHED':
            return {
                kind: 'proposal-limit-reached',
                statusCode,
                limit: readFiniteNumber(details?.limit)
            };
        case 'RATE_LIMITED':
            return {
                kind: 'rate-limited',
                statusCode,
                retryAfterSeconds: readFiniteNumber(details?.retryAfterSeconds)
            };
        case 'INVALID_BODY':
        case 'INVALID_QUERY':
            return { kind: 'invalid-request', statusCode, code, fields: readFields(details) };
        default:
            return { kind: 'coded', statusCode, code };
    }
}
